import { useState } from "react";
import { CircuitBoard, Cpu, KeyRound, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Pill } from "@/components/ui/pill";
import { Notify, toast } from "@/components/ui/notify";
import { isApiClientError } from "@/lib/api-client";
import {
  useDeleteDevice,
  useRotateDeviceKey,
  type DeviceSummary,
  type CreatedDeviceResponse,
} from "@/features/devices/hooks";
import { useDeleteCard, type CardSummary } from "@/features/cards/hooks";
import type { SensorsByCardId } from "@/types/device-manager.types";
import { Breadcrumb } from "./Breadcrumb";
import { ChildRow } from "./ChildRow";
import { UuidLine } from "./UuidLine";
import { ClpEditForm } from "./ClpEditForm";
import { NewCardForm } from "./NewCardForm";
import { RevealedDeviceKeyCard } from "./RevealedDeviceKeyCard";
import { devicePresence, PRESENCE_LABEL, PRESENCE_TONE } from "./device-presence";

interface ClpDetailPaneProps {
  device: DeviceSummary;
  cards: CardSummary[];
  sensorsByCardId: SensorsByCardId;
  onSelectCard: (cardId: string) => void;
  onDirtyChange: (dirty: boolean) => void;
  onDeleted: () => void;
}

export function ClpDetailPane({
  device,
  cards,
  sensorsByCardId,
  onSelectCard,
  onDirtyChange,
  onDeleted,
}: ClpDetailPaneProps) {
  const [showNewCard, setShowNewCard] = useState(false);
  const [revealed, setRevealed] = useState<CreatedDeviceResponse | null>(null);
  const rotate = useRotateDeviceKey();
  const del = useDeleteDevice();
  const delCard = useDeleteCard(device.id);

  async function onRotate() {
    const ok = await Notify.confirm({
      variant: "warning",
      title: `Rotacionar a chave do CLP "${device.code}"?`,
      description: "A chave anterior será invalidada imediatamente.",
      confirmLabel: "Rotacionar",
    });
    if (!ok) return;
    try {
      setRevealed(await rotate.mutateAsync(device.id));
      toast.success("Chave rotacionada.");
    } catch (err) {
      toast.error("Falha ao rotacionar.", {
        description: isApiClientError(err) ? err.error.message : "Erro inesperado.",
      });
    }
  }

  async function onDelete() {
    const ok = await Notify.confirm({
      variant: "danger",
      title: `Excluir CLP "${device.code}"?`,
      confirmLabel: "Excluir",
      typeToConfirm: device.code,
    });
    if (!ok) return;
    try {
      await del.mutateAsync(device.id);
      toast.success("CLP excluído.");
      onDeleted();
    } catch (err) {
      toast.error("Falha ao excluir.", {
        description: isApiClientError(err) ? err.error.message : "Erro inesperado.",
      });
    }
  }

  async function onDeleteCard(id: string, code: string) {
    const ok = await Notify.confirm({
      variant: "danger",
      title: `Excluir cartão "${code}"?`,
      confirmLabel: "Excluir",
      typeToConfirm: code,
    });
    if (!ok) return;
    try {
      await delCard.mutateAsync(id);
      toast.success("Cartão excluído.");
    } catch (err) {
      toast.error("Falha ao excluir.", {
        description: isApiClientError(err) ? err.error.message : "Erro inesperado.",
      });
    }
  }

  const sensorTotal = cards.reduce((a, c) => a + (sensorsByCardId.get(c.id)?.length ?? 0), 0);
  const presence = devicePresence(device.lastSeenAt);
  return (
    <div className="space-y-5">
      <Breadcrumb parts={[{ label: device.code, mono: true }]} />
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-[12px] text-text-secondary">
            <Cpu className="h-3.5 w-3.5" /> Controlador (CLP)
          </div>
          <h2 className="font-mono text-[20px] font-semibold text-text-primary">{device.code}</h2>
          <UuidLine uuid={device.id} />
          <div className="mt-1 flex items-center gap-2">
            <Pill tone={PRESENCE_TONE[presence]} withDot>
              {PRESENCE_LABEL[presence]}
            </Pill>
            {device.lastSeenAt && (
              <span className="text-[11px] text-text-tertiary">
                visto {new Date(device.lastSeenAt).toLocaleString("pt-BR")}
              </span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button variant="outline" size="sm" onClick={() => void onRotate()}>
            <KeyRound className="h-4 w-4" /> Rotacionar chave
          </Button>
          <Button variant="ghost" size="sm" onClick={() => void onDelete()}>
            <Trash2 className="h-4 w-4" /> Excluir CLP
          </Button>
        </div>
      </div>

      {revealed && <RevealedDeviceKeyCard revealed={revealed} onClose={() => setRevealed(null)} />}

      <section className="space-y-3">
        <div className="text-[13px] font-medium text-text-secondary">Identificação</div>
        <ClpEditForm device={device} onDirtyChange={onDirtyChange} />
      </section>

      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-[13px] font-medium text-text-secondary">
            Cartões de I/O <span className="text-text-tertiary">· {cards.length}</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowNewCard((v) => !v)}>
            {showNewCard ? "Fechar" : "Novo cartão"}
          </Button>
        </div>
        {showNewCard && <NewCardForm deviceId={device.id} onDone={() => setShowNewCard(false)} />}
        <div className="space-y-1.5">
          {cards.map((c) => (
            <ChildRow
              key={c.id}
              icon={<CircuitBoard className="h-4 w-4" />}
              code={c.code}
              meta={`${sensorsByCardId.get(c.id)?.length ?? 0} sensor(es)`}
              onOpen={() => onSelectCard(c.id)}
              onDelete={() => void onDeleteCard(c.id, c.code)}
            />
          ))}
          {cards.length === 0 && (
            <p className="text-[13px] text-text-secondary">Nenhum cartão neste CLP.</p>
          )}
        </div>
      </section>

      <div className="flex gap-6 border-t border-border-subtle pt-3 text-[13px]">
        <span>
          <span className="font-mono text-text-primary">{cards.length}</span>{" "}
          <span className="text-text-tertiary">cartões</span>
        </span>
        <span>
          <span className="font-mono text-text-primary">{sensorTotal}</span>{" "}
          <span className="text-text-tertiary">sensores</span>
        </span>
      </div>
    </div>
  );
}
